<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'property',
    path: '/properties/:id',
    component: 'Property',
    params: [
      {
        name: 'id',
        prop: 'propertyId',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Property',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    propertyId: {
      type: String,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const ux = ref({
    initialLoadSpinner: true,
  });
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_parentTo = computed(() => ({
    name: 'app.properties',
  }));

  const $data = computed(() => ({
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_parentTo: b_2248226175642056_parentTo.value,
  }));

  const $$load_item = async () => {
    return $fn.value.fetch('read_property', {
      asResource: true,
      parameters: {
        id: props.propertyId,
        include_units: true,
        include_subtree: true,
      },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_parentTo', nodeId: '2248226175642056' },
    ],
    loaders: { item: $$load_item },
    ux: { initialLoadSpinner: true },
    dataRefs: {
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_parentTo,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [item.value.snapshot('name'), $fn.value.gettext('Property')],
        icon: $config.value.property.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.propertyId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('propertyId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_s16mhoynm_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_s16mhoynm =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_s16mhoynm_getActionArgs,
    });
  const inst_evh_7315092382398562_close_ru7ttkokm =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_s16mhoynm,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_ru7ttkokm],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_va30gi7c4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_8wlx3akgm =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_va30gi7c4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_8wlx3akgm],
    event: 'reload',
  });
  const act_6466705138206980_reloadComponentData_78v15eoi6 =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_6466705138206980_clickPreventStop_ulwsc6m14 =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_78v15eoi6,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_6466705138206980_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6466705138206980_clickPreventStop_ulwsc6m14],
      event: 'click.prevent.stop',
    });
  const act_8320016629450276_closeComponent_772jzb5e1 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8320016629450276_success_g5hg3bcx4 =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_closeComponent_772jzb5e1,
      event: 'success',
      displayName: 'closeComponent',
    });
  const inst_evh_8320016629450276_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8320016629450276_success_g5hg3bcx4],
    event: 'success',
  });
  const act_5477420070495986_reloadComponentData_jkjtvgovc =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5477420070495986_reload_psncfa6dg =
    new actionTypes.EventHandlerImpl({
      action: act_5477420070495986_reloadComponentData_jkjtvgovc,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5477420070495986_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5477420070495986_reload_psncfa6dg],
    event: 'reload',
  });
  const act_6337614691287098_reloadComponentData_wrcq14djy =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6337614691287098_reload_sssswotgx =
    new actionTypes.EventHandlerImpl({
      action: act_6337614691287098_reloadComponentData_wrcq14djy,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6337614691287098_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6337614691287098_reload_sssswotgx],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_ru7ttkokm,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_8wlx3akgm,
    inst_evh_7315092382398562_reload,
    inst_evh_6466705138206980_clickPreventStop_ulwsc6m14,
    reload: inst_evh_6466705138206980_clickPreventStop_ulwsc6m14,
    inst_evh_6466705138206980_clickPreventStop,
    inst_evh_8320016629450276_success_g5hg3bcx4,
    inst_evh_8320016629450276_success,
    inst_evh_5477420070495986_reload_psncfa6dg,
    inst_evh_5477420070495986_reload,
    inst_evh_6337614691287098_reload_sssswotgx,
    inst_evh_6337614691287098_reload,
  };

  const evh_7315092382398562_close_ru7ttkokm = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_ru7ttkokm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_8wlx3akgm = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_8wlx3akgm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_clickPreventStop_ulwsc6m14 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_clickPreventStop_ulwsc6m14.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_success_g5hg3bcx4 = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_success_g5hg3bcx4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_success = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5477420070495986_reload_psncfa6dg = async (event, scope) => {
    return await actions.inst_evh_5477420070495986_reload_psncfa6dg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5477420070495986_reload = async (event, scope) => {
    return await actions.inst_evh_5477420070495986_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6337614691287098_reload_sssswotgx = async (event, scope) => {
    return await actions.inst_evh_6337614691287098_reload_sssswotgx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6337614691287098_reload = async (event, scope) => {
    return await actions.inst_evh_6337614691287098_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Property"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <MaterialTabs
      :title="item.snapshot('name')"
      :parentText="gettext('Properties')"
      :parentTo="b_2248226175642056_parentTo"
      class="h-100"
      v-if="loaded"
    >
      <template #actions>
        <ItpButton
          variant="light"
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_6466705138206980_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <BtnDeleteResource
          :resource="item"
          :dialogHeader="pgettext('Contract/DialogTitle', 'Delete property')"
          @success="evh_8320016629450276_success($event, {})"
        >
        </BtnDeleteResource>
      </template>
      <BTab :title="gettext('General')" class="container float-start">
        <ItpRow>
          <ItpCol :w="6">
            <UcPropertyGeneralData
              :property="item"
              :reload="lastLoaded"
              class="pb-4"
              @reload="evh_5477420070495986_reload($event, {})"
            >
            </UcPropertyGeneralData>
            <UcPropertyUnits
              :parent="item"
              :reload="lastLoaded"
              class="pb-4"
              @reload="evh_6337614691287098_reload($event, {})"
            >
            </UcPropertyUnits>
          </ItpCol>
          <ItpCol :w="6">
            <UcCommentsList :parent="item" :reload="lastLoaded" class="pb-4">
            </UcCommentsList>
            <UcAttachmentsList :parent="item" :reload="lastLoaded" class="pb-4">
            </UcAttachmentsList>
          </ItpCol>
        </ItpRow>
      </BTab>
    </MaterialTabs>
  </ItpPage>
</template>
